(() => {
  if (window.__protemoaImageLoadingPriority) return;

  const contentQueue = [];
  const deferredQueue = [];
  const queuedImages = new WeakSet();
  const imagePreloadRequests = new WeakMap();
  const contentConcurrency = 3;
  const deferredConcurrency = 2;
  let activeContentImages = 0;
  let activeDeferredImages = 0;
  let backgroundLoadingStarted = false;
  let backgroundTaskScheduled = false;
  let mutationScanScheduled = false;

  const runWhenIdle = (callback) => {
    if (window.requestIdleCallback) {
      window.requestIdleCallback(callback, { timeout: 1200 });
      return;
    }

    window.setTimeout(
      () => callback({ didTimeout: true, timeRemaining: () => 0 }),
      160
    );
  };

  const hasImageSource = (image) =>
    Boolean(image.currentSrc || image.getAttribute('src') || image.getAttribute('srcset'));

  const isRendered = (element) => {
    if (!element?.isConnected || !element.getClientRects().length) return false;

    const styles = window.getComputedStyle(element);
    return styles.display !== 'none' && styles.visibility !== 'hidden';
  };

  const isInViewport = (element) => {
    if (!isRendered(element)) return false;

    const bounds = element.getBoundingClientRect();
    return (
      bounds.width > 0 &&
      bounds.height > 0 &&
      bounds.bottom > 0 &&
      bounds.right > 0 &&
      bounds.top < window.innerHeight &&
      bounds.left < window.innerWidth
    );
  };

  const setImageTier = (image, tier) => {
    image.dataset.protemoaLoadTier = tier;

    if (tier === 'critical') {
      image.loading = 'eager';
      image.fetchPriority = 'high';
      image.setAttribute('fetchpriority', 'high');
      return;
    }

    if (tier === 'content' || tier === 'deferred') {
      const priority = tier === 'content' ? 'auto' : 'low';
      image.fetchPriority = priority;
      image.setAttribute('fetchpriority', priority);
    }
  };

  const findPrimaryContentImage = (root = document) => {
    const main = root === document
      ? document.getElementById('MainContent')
      : root.closest?.('#MainContent') || root.querySelector?.('#MainContent') || root;

    if (!main?.querySelectorAll) return null;
    if (main.querySelector('img[fetchpriority="high"], img[data-protemoa-load-tier="critical"]')) {
      return null;
    }

    const candidates = Array.from(main.querySelectorAll('img')).filter((image) => {
      if (!hasImageSource(image) || !isInViewport(image)) return false;
      if (image.closest('.protemoa-desktop-gallery-nav')) return false;

      const cardSlide = image.closest('[data-carousel-slide]');
      if (cardSlide) {
        const carousel = cardSlide.closest('product-card-carousel');
        const slides = carousel
          ? Array.from(carousel.querySelectorAll('[data-carousel-slide]'))
          : [];
        if (slides.indexOf(cardSlide) > 0) return false;
      }

      if (image.closest('.card__media .imgEffect') && image.classList.contains('second')) {
        return false;
      }

      const bounds = image.getBoundingClientRect();
      return bounds.width >= 120 && bounds.height >= 120;
    });

    return candidates.reduce((largest, image) => {
      if (!largest) return image;
      const bounds = image.getBoundingClientRect();
      const largestBounds = largest.getBoundingClientRect();
      return bounds.width * bounds.height > largestBounds.width * largestBounds.height
        ? image
        : largest;
    }, null);
  };

  const prioritizePrimaryContentImage = (root = document) => {
    const primaryImage = findPrimaryContentImage(root);
    if (primaryImage) setImageTier(primaryImage, 'critical');
  };

  const getCardImageTier = (image) => {
    const slide = image.closest('[data-carousel-slide]');
    if (!slide) return null;

    const carousel = slide.closest('product-card-carousel');
    if (!carousel) return null;

    const slides = Array.from(carousel.querySelectorAll('[data-carousel-slide]'));
    return slides.indexOf(slide) === 0 ? 'content' : 'deferred';
  };

  const getLegacyCardImageTier = (image) => {
    const legacyCardMedia = image.closest('.card__media .imgEffect');
    if (!legacyCardMedia) return null;

    return image.classList.contains('second') ? 'deferred' : 'content';
  };

  const getProductGalleryImageTier = (image) => {
    if (image.closest('.protemoa-desktop-gallery-nav')) return 'ignore';

    const mobileGallery = image.closest('.main-product-block .custom-swiper');
    const desktopGallery = image.closest('.main-product-block .old-sldier');
    const gallery = mobileGallery || desktopGallery;
    if (!gallery) return null;

    if (!isRendered(gallery)) return 'ignore';

    const selector = mobileGallery
      ? '.product__media-list .slider__slide img'
      : '.w3-content.w3-display-container > .mySlides img, .w3-content.w3-display-container > .mySlides1 img';
    const galleryImages = Array.from(gallery.querySelectorAll(selector));
    const imageIndex = galleryImages.indexOf(image);
    if (imageIndex < 0) return 'ignore';

    return imageIndex === 0 ? 'content' : 'deferred';
  };

  const getHeroImageTier = (image) => {
    const slide = image.closest('[data-protemoa-hero-slide]');
    if (!slide) return null;
    return isInViewport(image) ? 'content' : 'deferred';
  };

  const getImageTier = (image) => {
    if (image.getAttribute('fetchpriority') === 'high' || image.dataset.protemoaLoadTier === 'critical') {
      return 'critical';
    }

    return (
      getCardImageTier(image) ||
      getLegacyCardImageTier(image) ||
      getProductGalleryImageTier(image) ||
      getHeroImageTier(image) ||
      (isRendered(image) ? 'content' : 'deferred')
    );
  };

  const waitForImage = (image) => {
    if (image.complete && image.naturalWidth) return Promise.resolve();
    if (typeof image.decode === 'function') return image.decode().catch(() => {});

    return new Promise((resolve) => {
      image.addEventListener('load', resolve, { once: true });
      image.addEventListener('error', resolve, { once: true });
    });
  };

  const preloadImageSource = (image) => {
    if (image.complete && image.naturalWidth) return Promise.resolve();

    const existingRequest = imagePreloadRequests.get(image);
    if (existingRequest) return existingRequest;

    const source = image.currentSrc || image.getAttribute('src');
    const sourceSet = image.getAttribute('srcset');
    if (!source && !sourceSet) return Promise.resolve();

    const preload = new Image();
    const sizes = image.getAttribute('sizes');
    if (sizes) preload.sizes = sizes;
    preload.decoding = 'async';
    preload.fetchPriority = image.fetchPriority || 'auto';
    preload.setAttribute('fetchpriority', image.getAttribute('fetchpriority') || 'auto');
    if (sourceSet) preload.srcset = sourceSet;
    if (source) preload.src = source;

    const request = waitForImage(preload).finally(() => {
      imagePreloadRequests.delete(image);
    });
    imagePreloadRequests.set(image, request);
    return request;
  };

  const scheduleBackgroundLoading = () => {
    const hasContentWork = contentQueue.length > 0 || activeContentImages > 0;
    const queue = hasContentWork ? contentQueue : deferredQueue;
    const activeImages = hasContentWork ? activeContentImages : activeDeferredImages;
    const concurrency = hasContentWork ? contentConcurrency : deferredConcurrency;

    if (backgroundTaskScheduled || !queue.length || activeImages >= concurrency) return;

    backgroundTaskScheduled = true;
    runWhenIdle((deadline) => {
      backgroundTaskScheduled = false;

      const shouldLoadContent = contentQueue.length > 0 || activeContentImages > 0;
      const selectedQueue = shouldLoadContent ? contentQueue : deferredQueue;
      const selectedConcurrency = shouldLoadContent ? contentConcurrency : deferredConcurrency;
      let availableSlots = selectedConcurrency - (
        shouldLoadContent ? activeContentImages : activeDeferredImages
      );

      while (
        availableSlots > 0 &&
        selectedQueue.length &&
        (deadline.didTimeout || deadline.timeRemaining() > 4)
      ) {
        const image = selectedQueue.shift();
        if (!image?.isConnected) continue;

        availableSlots -= 1;
        if (shouldLoadContent) activeContentImages += 1;
        else activeDeferredImages += 1;

        setImageTier(image, shouldLoadContent ? 'content' : 'deferred');
        image.loading = 'eager';

        Promise.all([waitForImage(image), preloadImageSource(image)]).finally(() => {
          if (shouldLoadContent) activeContentImages -= 1;
          else activeDeferredImages -= 1;
          scheduleBackgroundLoading();
        });
      }

      scheduleBackgroundLoading();
    });
  };

  const queueImage = (image) => {
    if (!hasImageSource(image) || queuedImages.has(image)) return;

    const tier = getImageTier(image);
    if (tier === 'ignore') return;

    setImageTier(image, tier);
    queuedImages.add(image);

    if (tier === 'content') contentQueue.push(image);
    if (tier === 'deferred') deferredQueue.push(image);
  };

  const scanImages = (root = document) => {
    if (!root?.querySelectorAll) return;

    prioritizePrimaryContentImage(root);

    if (root.matches?.('img')) queueImage(root);
    root.querySelectorAll('img').forEach(queueImage);
    scheduleBackgroundLoading();
  };

  const scheduleMutationScan = () => {
    if (mutationScanScheduled) return;
    mutationScanScheduled = true;

    window.requestAnimationFrame(() => {
      mutationScanScheduled = false;
      scanImages(document);
    });
  };

  const startBackgroundLoading = () => {
    if (backgroundLoadingStarted) return;
    backgroundLoadingStarted = true;
    scanImages(document);

    const observer = new MutationObserver((mutations) => {
      const needsScan = mutations.some((mutation) => {
        if (mutation.type === 'attributes') return true;
        return Array.from(mutation.addedNodes).some(
          (node) => node.nodeType === Node.ELEMENT_NODE
        );
      });

      if (needsScan) scheduleMutationScan();
    });
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['src', 'srcset'],
      childList: true,
      subtree: true,
    });
  };

  window.__protemoaImageLoadingPriority = {
    scan: scanImages,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => prioritizePrimaryContentImage(), { once: true });
  } else {
    prioritizePrimaryContentImage();
  }

  if (document.readyState === 'complete') {
    window.setTimeout(startBackgroundLoading, 0);
  } else {
    window.addEventListener('load', startBackgroundLoading, { once: true });
  }

  document.addEventListener('shopify:section:load', (event) => {
    if (backgroundLoadingStarted) scanImages(event.target);
    else prioritizePrimaryContentImage(event.target);
  });
  window.addEventListener('resize', scheduleMutationScan, { passive: true });
})();
